/**
 * Saved Searches functionality for Cycle Exchange
 * Save Your Search on collection and search pages. Logged-in customers save in one tap;
 * everyone else gets a small email popup, so nobody is sent away to log in.
 * The account page uses getSavedSearches / deleteSearch.
 */

const SavedSearches = {
  apiUrl: null,
  customerEmail: '',
  customerSig: '',
  customerLoggedIn: false,
  collectionTitle: '',
  collectionType: '',
  isSearchPage: false,
  searchQuery: '',
  storageKey: 'cx_alert_email',

  init(config) {
    this.apiUrl = config.apiUrl;
    this.customerEmail = config.customerEmail || '';
    this.customerSig = config.customerSig || '';
    this.customerLoggedIn = config.customerLoggedIn;
    this.collectionTitle = config.collectionTitle || '';
    this.collectionType = config.collectionType || '';

    this.isSearchPage = window.location.pathname === '/search';
    this.searchQuery = new URLSearchParams(window.location.search).get('q') || '';

    // One delegated listener: buttons inside the filter sidebar are redrawn by AJAX
    if (this.bound) return;
    this.bound = true;
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-save-search]');
      if (!btn) return;
      e.preventDefault();
      this.handleSaveClick(btn);
    });
  },

  handleSaveClick(btn) {
    if (this.saving) return;
    if (this.customerLoggedIn && this.customerEmail) {
      this.saveAsCustomer(btn);
      return;
    }
    this.openPopup(btn);
  },

  // Generate readable name from URL params
  generateSearchName() {
    const params = new URLSearchParams(window.location.search);
    const parts = [];


    // Lead with what they're shopping for: the search words, a product type filter,
    // the collection's single product type ("Road Bikes"), or the collection title
    // without "Used" / "Second Hand".
    const typeFilter = params.getAll('filter.p.product_type').filter(Boolean);
    if (this.isSearchPage && this.searchQuery) {
      parts.push(`"${this.searchQuery.trim()}"`);
    }
    if (typeFilter.length) {
      parts.push(typeFilter.map((t) => this.pluralType(t)).join(' / '));
    } else if (!this.isSearchPage) {
      const base = this.collectionType
        ? this.pluralType(this.collectionType)
        : this.collectionTitle.replace(/^(used|second[ -]hand|pre-owned)\s+/i, '').replace(/\s+for sale$/i, '');
      if (base) parts.push(base.charAt(0).toUpperCase() + base.slice(1));
    }

    // Other storefront filters (filter.v.* / filter.p.*). Values of one filter are joined
    // ("54cm / 56cm"). Grouped values arrive as gid://shopify/FilterSettingGroup/<id>, so they
    // are named from their checkbox label; Rider height reads "Rider height XS / S".
    let priceMin = null;
    let priceMax = null;
    const byFilter = new Map();
    for (const [key, value] of params) {
      if (!value || !key.startsWith('filter.')) continue;
      if (key === 'filter.v.availability' || key === 'filter.p.product_type') continue;
      if (key === 'filter.v.price.gte') { priceMin = value; continue; }
      if (key === 'filter.v.price.lte') { priceMax = value; continue; }
      let cleanValue = value;
      if (value.startsWith('gid://')) {
        cleanValue = this.filterLabel(key, value);
        if (!cleanValue) continue;
      }
      if (key.endsWith('.size_guide')) cleanValue = cleanValue.replace(/\s*\(.*\)\s*$/, '');
      if (!cleanValue || cleanValue === this.collectionTitle) continue;
      if (!byFilter.has(key)) byFilter.set(key, []);
      const vals = byFilter.get(key);
      if (!vals.includes(cleanValue)) vals.push(cleanValue);
    }
    for (const [key, vals] of byFilter) {
      const joined = (key.endsWith('.size_guide') ? 'Rider height ' : '') + vals.join(' / ');
      if (!parts.includes(joined)) parts.push(joined);
    }
    if (priceMin || priceMax) {
      const fmt = (v) => '£' + parseInt(v, 10).toLocaleString('en-GB');
      if (priceMin && priceMax) parts.push(`${fmt(priceMin)}-${fmt(priceMax)}`);
      else if (priceMax) parts.push(`Up to ${fmt(priceMax)}`);
      else parts.push(`From ${fmt(priceMin)}`);
    }

    // Parse Searchanise rb_ parameters (searches saved before the native switch)
    for (const [key, value] of params) {
      if (!value) continue;
      if (key.startsWith('filter.')) continue;

      // Price range: rb_price=2495,3000
      if (key === 'rb_price') {
        const [min, max] = value.split(',');
        if (min && max) {
          parts.push(`£${parseInt(min).toLocaleString()}-£${parseInt(max).toLocaleString()}`);
        } else if (min) {
          parts.push(`From £${parseInt(min).toLocaleString()}`);
        }
        continue;
      }

      // Product type: rb_product_type=Gravel/CX Bike
      if (key === 'rb_product_type') {
        const cleanValue = decodeURIComponent(value).replace(/\//g, '/');
        if (!parts.includes(cleanValue) && cleanValue !== this.collectionTitle) {
          parts.push(cleanValue);
        }
        continue;
      }

      // Size: rb_snize_facet1=XS (47cm - 50cm)
      if (key === 'rb_snize_facet1' || key.includes('size')) {
        const cleanValue = decodeURIComponent(value);
        parts.push(cleanValue);
        continue;
      }

      // Brand: rb_snize_facet2 or similar
      if (key === 'rb_snize_facet2' || key.includes('brand')) {
        const cleanValue = decodeURIComponent(value);
        parts.push(cleanValue);
        continue;
      }

      // Material: rb_snize_facet3 or similar
      if (key === 'rb_snize_facet3' || key.includes('material')) {
        const cleanValue = decodeURIComponent(value);
        parts.push(cleanValue);
        continue;
      }

      // Groupset: rb_snize_facet4 or similar
      if (key === 'rb_snize_facet4' || key.includes('groupset')) {
        const cleanValue = decodeURIComponent(value);
        parts.push(cleanValue);
        continue;
      }

      // Generic rb_ params we haven't specifically handled
      if (key.startsWith('rb_') && key !== 'rb_page' && !key.includes('sort')) {
        const cleanValue = decodeURIComponent(value)
          .replace(/%20/g, ' ')
          .replace(/\+/g, ' ');
        if (cleanValue && !parts.includes(cleanValue)) {
          parts.push(cleanValue);
        }
      }
    }

    // If we have parts, join them; otherwise use fallback
    if (parts.length > 0) {
      // Limit length - take first 5 parts max
      const name = parts.slice(0, 5).join(' · ');
      return name;
    }

    // Fallback if no parts at all
    return this.collectionTitle || 'Saved Search';
  },

  // "Road Bike" -> "Road Bikes", "MTB" -> "Mountain Bikes"
  pluralType(type) {
    const t = String(type).trim();
    if (/^mtb$/i.test(t)) return 'Mountain Bikes';
    if (/^hybrid$/i.test(t)) return 'Hybrid Bikes';
    if (/(bike|frameset|frame|wheelset|wheel)$/i.test(t)) return t + 's';
    return t;
  },

  // Label of a filter checkbox, without the trailing "(count)"
  filterLabel(key, value) {
    const input = Array.from(document.querySelectorAll('input[name]')).find((i) => i.name === key && i.value === value);
    const label = input && (input.closest('label') || document.querySelector(`label[for="${input.id}"]`));
    if (!label) return '';
    return label.textContent.replace(/\s+/g, ' ').replace(/\s*\(\d+\)\s*$/, '').trim();
  },

  // { "<gid>": "<label>" } for grouped filter values in the URL (the worker matches on the label)
  groupedLabels() {
    const labels = {};
    for (const [key, value] of new URLSearchParams(window.location.search)) {
      if (!key.startsWith('filter.') || !value.startsWith('gid://')) continue;
      const label = this.filterLabel(key, value);
      if (label) labels[value] = label;
    }
    return labels;
  },

  // POST the current search to the worker. Handles the current worker's replies
  // ({success} / 409 {duplicate}) and the newer {status} replies.
  async submit(email) {
    const body = {
      action: 'save',
      email: email,
      source: this.customerLoggedIn ? 'customer' : 'guest',
      search: {
        name: this.generateSearchName(),
        url: window.location.href,
        collection: window.location.pathname,
        labels: this.groupedLabels(),
      },
    };
    if (this.customerSig && email.toLowerCase() === this.customerEmail.toLowerCase()) {
      body.customer_sig = this.customerSig;
    }

    let response;
    let data = {};
    try {
      response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      data = await response.json().catch(() => ({}));
    } catch (error) {
      console.error('SavedSearches: Error saving', error);
      return 'error';
    }

    if (data.status === 'duplicate' || data.duplicate || response.status === 409) return 'duplicate';
    if (data.status === 'pending_confirmation') return 'pending';
    if (response.ok && (data.status === 'saved' || data.success)) return 'saved';
    if (data.error === 'rate_limited' || response.status === 429) return 'rate_limited';
    if (data.error === 'invalid_email') return 'invalid_email';
    return 'error';
  },

  // Logged-in customers: one tap, no popup
  async saveAsCustomer(btn) {
    const originalHtml = btn.innerHTML;
    this.saving = true;
    btn.innerHTML = '<span class="save-search-btn__spinner"></span> Saving...';

    const result = await this.submit(this.customerEmail);
    this.saving = false;

    if (result === 'saved' || result === 'pending') {
      this.markSaved(btn);
      this.showToast('Search saved! We\'ll email you when a match lands.', 'success');
    } else if (result === 'duplicate') {
      btn.innerHTML = originalHtml;
      this.showToast('You\'ve already saved this search.', 'info');
    } else {
      btn.innerHTML = originalHtml;
      this.showToast(result === 'rate_limited' ? 'You\'ve saved a lot of searches today. Please try again tomorrow.' : 'Unable to save search. Please try again.', 'error');
    }
  },

  markSaved(btn) {
    if (!btn) return;
    if (btn.classList.contains('cx-save-search__btn')) {
      btn.innerHTML = '<span class="cx-save-search__title">Search saved</span><span class="cx-save-search__note">We\'ll email you when a matching bike lands.</span>';
    } else {
      btn.textContent = 'Alert saved';
    }
    btn.classList.add('save-search-btn--saved');
  },

  // ---------- Guest popup ----------
  buildPopup() {
    const el = document.createElement('div');
    el.className = 'cx-alert';
    el.hidden = true;
    el.innerHTML = `
      <div class="cx-alert__backdrop" data-alert-close></div>
      <div class="cx-alert__panel" role="dialog" aria-modal="true" aria-labelledby="cx-alert-title">
        <button type="button" class="cx-alert__close" data-alert-close aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
        </button>
        <div data-alert-step="form">
          <p class="cx-alert__eyebrow">Bike alert</p>
          <h2 class="cx-alert__title" id="cx-alert-title">Get an email when it lands</h2>
          <p class="cx-alert__summary" data-alert-summary></p>
          <form class="cx-alert__form" novalidate>
            <label class="cx-alert__label" for="cx-alert-email">Your email address</label>
            <div class="cx-alert__row">
              <input id="cx-alert-email" class="cx-alert__input" type="email" name="email" autocomplete="email" inputmode="email" required placeholder="Your email address">
              <button type="submit" class="cx-alert__submit">Alert me</button>
            </div>
            <p class="cx-alert__error" data-alert-error role="alert" hidden></p>
            <p class="cx-alert__legal">By saving you agree to emails from Cycle Exchange, including offers. Unsubscribe any time.</p>
          </form>
        </div>
        <div data-alert-step="done" hidden>
          <p class="cx-alert__eyebrow" data-alert-done-eyebrow>Alert saved</p>
          <h2 class="cx-alert__title" data-alert-done-title></h2>
          <p class="cx-alert__text" data-alert-done-text></p>
          <button type="button" class="cx-alert__submit cx-alert__submit--full" data-alert-close>Keep browsing</button>
        </div>
      </div>`;
    document.body.appendChild(el);

    el.addEventListener('click', (e) => {
      if (e.target.closest('[data-alert-close]')) this.closePopup();
    });
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closePopup();
      if (e.key !== 'Tab') return;
      const focusable = Array.from(el.querySelectorAll('button, input')).filter((f) => f.offsetParent !== null);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    el.querySelector('form').addEventListener('submit', (e) => {
      e.preventDefault();
      this.submitPopup();
    });
    return el;
  },

  openPopup(btn) {
    this.popup = this.popup || this.buildPopup();
    const el = this.popup;
    this.activeBtn = btn;
    this.lastFocus = document.activeElement;

    el.querySelector('[data-alert-summary]').textContent = this.generateSearchName();
    el.querySelector('[data-alert-step="form"]').hidden = false;
    el.querySelector('[data-alert-step="done"]').hidden = true;
    this.showError('');

    const input = el.querySelector('input[type="email"]');
    let remembered = '';
    try { remembered = localStorage.getItem(this.storageKey) || ''; } catch (e) {}
    if (remembered && !input.value) input.value = remembered;

    el.hidden = false;
    document.documentElement.classList.add('cx-alert-open');
    this.fitToViewport();
    if (this.fitPopup) this.fitPopup();
    requestAnimationFrame(() => el.classList.add('is-open'));
    (input.value ? el.querySelector('.cx-alert__submit') : input).focus({ preventScroll: true });
  },

  // Keep the popup inside the visible part of the screen: on phones the keyboard shrinks
  // the visual viewport, and a plain position: fixed overlay would sit behind it.
  fitToViewport() {
    const vv = window.visualViewport;
    if (!vv || this.vvBound) return;
    this.vvBound = true;
    const fit = () => {
      if (!this.popup || this.popup.hidden) return;
      this.popup.style.top = vv.offsetTop + 'px';
      this.popup.style.height = vv.height + 'px';
      this.popup.style.bottom = 'auto';
    };
    vv.addEventListener('resize', fit);
    vv.addEventListener('scroll', fit);
    this.fitPopup = fit;
    fit();
  },

  closePopup() {
    const el = this.popup;
    if (!el || el.hidden) return;
    el.classList.remove('is-open');
    document.documentElement.classList.remove('cx-alert-open');
    setTimeout(() => { el.hidden = true; }, 200);
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
  },

  showError(message) {
    const err = this.popup && this.popup.querySelector('[data-alert-error]');
    if (!err) return;
    err.textContent = message;
    err.hidden = !message;
  },

  async submitPopup() {
    if (this.saving) return;
    const el = this.popup;
    const input = el.querySelector('input[type="email"]');
    const submitBtn = el.querySelector('.cx-alert__form .cx-alert__submit');
    const email = input.value.trim();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      this.showError('Please enter a valid email address.');
      input.focus();
      return;
    }
    this.showError('');
    this.saving = true;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Saving...';

    const result = await this.submit(email);
    this.saving = false;
    submitBtn.disabled = false;
    submitBtn.textContent = 'Alert me';

    const done = {
      saved: ['Alert saved', 'You\'re all set', 'We\'ll email {email} as soon as a matching bike lands.'],
      pending: ['Almost there', 'Check your inbox', 'Tap the link we\'ve sent to {email} to switch your alert on.'],
      duplicate: ['Already saved', 'You\'re on it already', 'Alerts for this search are already going to {email}.'],
    }[result];

    if (!done) {
      if (result === 'invalid_email') this.showError('Please enter a valid email address.');
      else if (result === 'rate_limited') this.showError('You\'ve saved a lot of searches today. Please try again tomorrow.');
      else this.showError('Something went wrong. Please try again.');
      return;
    }

    try { localStorage.setItem(this.storageKey, email); } catch (e) {}
    if (result !== 'duplicate') this.markSaved(this.activeBtn);

    el.querySelector('[data-alert-done-eyebrow]').textContent = done[0];
    el.querySelector('[data-alert-done-title]').textContent = done[1];
    el.querySelector('[data-alert-done-text]').textContent = done[2].replace('{email}', email);
    el.querySelector('[data-alert-step="form"]').hidden = true;
    el.querySelector('[data-alert-step="done"]').hidden = false;
    el.querySelector('[data-alert-step="done"] .cx-alert__submit').focus({ preventScroll: true });
  },

  // Show toast notification
  showToast(message, type = 'success') {
    // Remove existing toast
    const existing = document.querySelector('.save-search-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = `save-search-toast save-search-toast--${type}`;
    toast.innerHTML = `
      <span>${message}</span>
      ${type === 'success' ? '<a href="/account#saved" class="save-search-toast__link">View Saved Searches</a>' : ''}
    `;
    document.body.appendChild(toast);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      toast.classList.add('save-search-toast--hide');
      setTimeout(() => toast.remove(), 300);
    }, 5000);
  },

  // For account page: Get saved searches
  async getSavedSearches() {
    if (!this.apiUrl || !this.customerEmail) return { searches: [] };

    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get',
          email: this.customerEmail
        })
      });

      if (!response.ok) throw new Error('Failed to fetch');
      return response.json();

    } catch (error) {
      console.error('SavedSearches: Error fetching', error);
      return { searches: [], error: true };
    }
  },

  // For account page: Delete saved search
  async deleteSearch(searchId) {
    if (!this.apiUrl || !this.customerEmail) return false;

    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'delete',
          email: this.customerEmail,
          searchId: searchId
        })
      });

      return response.ok;

    } catch (error) {
      console.error('SavedSearches: Error deleting', error);
      return false;
    }
  }
};

// Export for use in other scripts
window.SavedSearches = SavedSearches;
